import React, { useEffect, useState } from 'react';
import Toast from '../../molecules/Toast';
import linkedinService from '../../../services/linkedinService';
import selectArrow from '../../../assets/arrow-down-img.svg';
import CenterModal from '../../molecules/Modal/CenterModal';
import Button from '../../molecules/Button';
import { StyledTagSelector } from './TagSelector.styles';
import AlterTag from './AlterTag';

const TagSelector = ({ selectedTag, setSelectedTag, required = false, label = 'Tag' }) => {
  const [tags, setTags] = useState([]);
  const [showTagInput, setShowTagInput] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [loadingTags, setLoadingTags] = useState(false);
  const [editingTagId, setEditingTagId] = useState(null);
  const [editingTagName, setEditingTagName] = useState('');
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => { },
    type: '',
  });

  useEffect(() => {
    fetchTags();
  }, []);

  useEffect(() => {
    const handleClickOutside = event => {
      if (showDropdown && !event.target.closest('.tag-dropdown-container')) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const fetchTags = async () => {
    try {
      setLoadingTags(true);
      const res = await linkedinService.getTags();
      if (res?.success && res?.data) {
        const formattedTags = res.data.map(tag => ({
          label: tag.name || tag.label,
          value: tag.id || tag.value || tag.name,
        }));
        setTags(formattedTags);
      }
    } catch (error) {
      Toast({
        type: 'error',
        message: error.message || 'Failed to fetch tags',
      });
    } finally {
      setLoadingTags(false);
    }
  };

  const handleAddTag = async () => {
    if (!newTagName.trim()) {
      Toast({
        type: 'error',
        message: 'Please enter a tag name',
      });
      return;
    }

    try {
      setLoadingTags(true);
      const res = await linkedinService.CreateTag({ name: newTagName.trim() });

      if (res?.success) {
        Toast({
          type: 'success',
          message: 'Tag created successfully',
        });

        await fetchTags();

        const newTagValue = res?.data?.id || res?.data?.value || newTagName.trim().toLowerCase();
        setSelectedTag(newTagValue);

        setNewTagName('');
        setShowTagInput(false);
      }
    } catch (error) {
      Toast({
        type: 'error',
        message: error.message || 'Failed to create tag',
      });
    } finally {
      setLoadingTags(false);
    }
  };

  const handleUpdateTag = async id => {
    if (!editingTagName.trim()) {
      Toast({ type: 'error', message: 'Please enter a tag name' });
      return;
    }
    const res = await linkedinService.GetTagRelations(id);
    const data = res?.data;

    setConfirmModal({
      isOpen: true,
      title: 'Rename Tag',
      message: `This tag is associated with ${data?.leadsCount} Leads. Are you sure you want to rename this tag to "${editingTagName.trim()}"?`,
      type: 'update',
      onConfirm: async () => {
        try {
          setLoadingTags(true);
          const res = await linkedinService.UpdateTag({ id, name: editingTagName.trim() });
          if (res?.success) {
            Toast({ type: 'success', message: 'Tag updated successfully' });
            await fetchTags();
            setEditingTagId(null);
            setEditingTagName('');
          }
        } catch (error) {
          Toast({ type: 'error', message: error.message || 'Failed to update tag' });
        } finally {
          setLoadingTags(false);
          setConfirmModal(prev => ({ ...prev, isOpen: false }));
        }
      },
    });
  };

  const handleDeleteTag = async (id, name, event) => {
    const res = await linkedinService.GetTagRelations(id);
    const data = res?.data;
    event.stopPropagation();
    setConfirmModal({
      isOpen: true,
      title: 'Delete Tag',
      message: `This tag is associated with ${data?.leadsCount} Leads. Are you sure you want to delete the tag "${name}"?`,
      type: 'delete',
      onConfirm: async () => {
        try {
          setLoadingTags(true);
          const res = await linkedinService.DeleteTag(id);
          if (res?.success) {
            Toast({ type: 'success', message: 'Tag deleted successfully' });
            if (selectedTag === id) setSelectedTag('');
            await fetchTags();
          }
        } catch (error) {
          Toast({ type: 'error', message: error.message || 'Failed to delete tag' });
        } finally {
          setLoadingTags(false);
          setConfirmModal(prev => ({ ...prev, isOpen: false }));
        }
      },
    });
  };

  const handleTagSelect = tagValue => {
    if (editingTagId) return;
    setSelectedTag(tagValue);
    setShowDropdown(false);
  };

  return (
    <StyledTagSelector className="tag-dropdown-container">
      <label className="label">
        {required && <span className="required">*</span>}{label}
      </label>
      <div onClick={() => !loadingTags && setShowDropdown(!showDropdown)} className={`select-box ${loadingTags ? 'disabled' : ''}`}>
        <span className={`select-text ${selectedTag ? 'selected' : ''}`}>
          {loadingTags
            ? 'Loading tags...'
            : selectedTag
              ? tags.find(t => t.value === selectedTag)?.label || 'Select a tag'
              : 'Select a tag'}
        </span>
        <span className="arrow-icon">
          <img src={selectArrow} width="10" height="8" alt="arrow" />
        </span>
      </div>

      {showDropdown && !loadingTags && (
        <div className="dropdown-menu">
          {tags.length > 0 ? (
            <>
              {tags.map(tag => (
                <div
                  key={tag.value}
                  onClick={() => handleTagSelect(tag.value)}
                  className={`tag-item ${selectedTag === tag.value ? 'selected' : ''}`}>
                  {editingTagId === tag.value ? (
                    <div className="inline-edit-container" onClick={e => e.stopPropagation()}>
                      <input
                        type="text"
                        value={editingTagName}
                        onChange={e => setEditingTagName(e.target.value)}
                        autoFocus
                        onKeyPress={e => e.key === 'Enter' && handleUpdateTag(tag.value)}
                      />
                      <div className="edit-actions">
                        <button type="button" className="save-btn" onClick={() => handleUpdateTag(tag.value)}>Save</button>
                        <button type="button" className="cancel-btn" onClick={() => setEditingTagId(null)}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <span className="tag-name">{tag.label}</span>
                      <div className="tag-actions">
                        <button
                          type="button"
                          className="action-btn edit-btn"
                          title="Rename"
                          onClick={e => {
                            e.stopPropagation();
                            setEditingTagId(tag.value);
                            setEditingTagName(tag.label);
                          }}>
                          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          className="action-btn delete-btn"
                          title="Delete"
                          onClick={e => handleDeleteTag(tag.value, tag.label, e)}>
                          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
              <div
                onClick={e => {
                  e.stopPropagation();
                  setShowTagInput(true);
                  setShowDropdown(false);
                }}
                className="add-tag-button">
                <span className="plus-icon">+</span> Add Tag
              </div>
            </>
          ) : (
            <div
              onClick={e => {
                e.stopPropagation();
                setShowTagInput(true);
                setShowDropdown(false);
              }}
              className="add-tag-button no-tags">
              <span className="plus-icon">+</span> Add Tag
            </div>
          )}
        </div>
      )}

      {showTagInput && (
        <div className="tag-input-container">
          <div className="input-group">
            <input
              type="text"
              value={newTagName}
              onChange={e => setNewTagName(e.target.value)}
              placeholder="Enter tag name"
              onKeyPress={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddTag();
                }
              }}
              autoFocus
            />
            <button type="button" onClick={handleAddTag} disabled={loadingTags} className="add-button">
              {loadingTags ? 'Adding...' : 'Add'}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowTagInput(false);
                setNewTagName('');
              }}
              disabled={loadingTags}
              className="cancel-button">
              Cancel
            </button>
          </div>
        </div>
      )}

      <CenterModal
        open={confirmModal.isOpen}
        setOpen={isOpen => setConfirmModal(prev => ({ ...prev, isOpen }))}
        title={confirmModal.title}
      >
        <AlterTag
          message={confirmModal.message}
          buttonTitle={loadingTags ? 'Please wait...' : 'Yes'}
          isDelete={confirmModal.type === 'delete'}
          onCancel={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
          onsubmit={confirmModal.onConfirm}
        />
      </CenterModal>
    </StyledTagSelector>
  );
};

export default TagSelector;